import { useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { useStandaloneContext } from "../context/StandaloneContext";
import { userAuth } from "../service/AuthService";
import { setAuthDetails, setUserData, setAllowedGames } from "../redux/features/accountSlice";
import { emitCasinoReady } from "../utils/postMessageBridge";
import { IS_MOCK, MOCK_USER_DATA } from "../utils/mockData";

/**
 * Detects standalone mode when a game route is loaded with
 * `?token=xxx&operatorId=xxx` in the URL. Authenticates the user,
 * stores auth in Redux/localStorage, then strips the token from the URL.
 */
export default function useStandaloneAuth() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isStandalone, isAuthenticating, setIsStandalone, setIsAuthenticating } =
    useStandaloneContext();
  const hasRun = useRef(false);

  const authenticate = useCallback(
    async (token, operatorId, homeUrl) => {
      setIsAuthenticating(true);

      localStorage.setItem("token", token);
      localStorage.setItem("standaloneMode", "true");
      localStorage.setItem("operatorId", JSON.stringify(operatorId));

      try {
        const res = await userAuth(operatorId);

        if (res.status === 200) {
          const authDetails = { ...res.data.data, token, homeUrl };
          localStorage.setItem("authDetails", JSON.stringify(authDetails));
          localStorage.setItem("authenticated", "yes");

          if (authDetails?.allowedGames) {
            dispatch(setAllowedGames(authDetails.allowedGames));
            localStorage.setItem("allowedGames", JSON.stringify(authDetails.allowedGames));
          }

          dispatch(setAuthDetails(authDetails));
          dispatch(setUserData(authDetails));
          emitCasinoReady(authDetails);
        }
      } catch (err) {
        // Auth API is down — store minimal auth so inhouse games can still load.
        // The token is already in localStorage; set basic user data so the app
        // doesn't hang on the loading spinner.
        const fallbackAuth = { token, homeUrl, operatorId };
        localStorage.setItem("authDetails", JSON.stringify(fallbackAuth));
        dispatch(setAuthDetails(fallbackAuth));
        dispatch(setUserData(fallbackAuth));
        console.error("Standalone auth failed, proceeding with token-only auth:", err);
      } finally {
        // Strip token from URL to prevent leakage in browser history
        const cleanParams = new URLSearchParams(searchParams);
        cleanParams.delete("token");
        const remaining = cleanParams.toString();
        const cleanPath = remaining
          ? `${location.pathname}?${remaining}`
          : location.pathname;
        navigate(cleanPath, { replace: true });

        setIsAuthenticating(false);
      }
    },
    [dispatch, navigate, location.pathname, searchParams, setIsAuthenticating]
  );

  useEffect(() => {
    if (hasRun.current) return;

    const token = searchParams.get("token");
    const operatorId = searchParams.get("operatorId");
    const homeUrl = searchParams.get("homeUrl");
    const isGameRoute =
      location.pathname.startsWith("/live-casino/") ||
      location.pathname.startsWith("/inhouse-games/");

    if (!isGameRoute || !token) return;

    hasRun.current = true;
    setIsStandalone(true);

    if (IS_MOCK) {
      localStorage.setItem("token", MOCK_USER_DATA.token);
      localStorage.setItem("authenticated", "yes");
      localStorage.setItem("standaloneMode", "true");
      localStorage.setItem("operatorId", JSON.stringify(MOCK_USER_DATA.operatorId));
      localStorage.setItem("authDetails", JSON.stringify(MOCK_USER_DATA));
      dispatch(setAuthDetails(MOCK_USER_DATA));
      dispatch(setUserData(MOCK_USER_DATA));
      emitCasinoReady(MOCK_USER_DATA);
      setIsAuthenticating(false);

      const cleanParams = new URLSearchParams(searchParams);
      cleanParams.delete("token");
      const remaining = cleanParams.toString();
      const cleanPath = remaining
        ? `${location.pathname}?${remaining}`
        : location.pathname;
      navigate(cleanPath, { replace: true });
      return;
    }

    authenticate(token, operatorId, homeUrl);
  }, [
    searchParams,
    location.pathname,
    authenticate,
    dispatch,
    navigate,
    setIsStandalone,
    setIsAuthenticating,
  ]);

  return { isStandalone, isAuthenticating, isAuthenticated: !isAuthenticating && isStandalone };
}
